"use client";

import { getSlots } from "@faire/slate/lib/getSlots";
import { FilterMenuContext } from "@faire/slate/private/filter/FilterMenuContext";
import { FilterPopupChip } from "@faire/slate/private/filter/FilterPopupChip";
import { FilterPopupPortal } from "@faire/slate/private/filter/FilterPopupPortal";
import { FilterPopupRoot } from "@faire/slate/private/filter/FilterPopupRoot";
import type {
  FilterMenuAlignment,
  IFilterMenuWebProps,
} from "@faire/slate/private/filter/types";
import { InstanceOfComponent } from "@faire/slate/private/shared/types";
import React, { useCallback, useId } from "react";

import { FilterMenuBody } from "./FilterMenuBody";

type IProps = {
  /**
   * Popup alignment relative to the chip.
   *
   * @default "start"
   */
  alignment?: FilterMenuAlignment;

  /**
   * Whether the filter currently has an applied value. Renders the chip's
   * X icon button and background if filter sets applied to true. When omitted,
   * the chip and background are not rendered after a filter apply.
   *
   * @default false
   */
  applied?: boolean;

  /**
   * The popup contents. Must be a `<FilterMenuBody>`.
   */
  children: InstanceOfComponent<typeof FilterMenuBody>;

  /**
   * Whether the filter menu trigger is disabled.
   *
   * @default false
   */
  disabled?: boolean;

  /**
   * The filter trigger label shown in the chip.
   */
  label: string;

  /**
   * Whether the popup is open. Provide to control the popup's open state from
   * the consumer; pair with `onClose` to react to close requests. When omitted,
   * the popup manages its own open state via the chip.
   */
  open?: boolean;

  /**
   * **web**
   * Fired when the chip is clicked
   */
  onClick?: () => void;

  /**
   * **web**
   * Fired when either the chip's X icon or the popup header's Clear button is
   * clicked. The popup closes automatically afterwards.
   */
  onClear?: () => void;

  /**
   * **web**
   * Fired when the popup requests to close (outside click, Escape, Clear, or
   * footer action).
   *
   * Pair with `open` to control the popup's open state from the consumer.
   */
  onClose?: () => void;

  // DO NOT ADD any new prop without aligning with Slate team first
} & IFilterMenuWebProps;

/**
 * Filter Menu exposes a chip + dropdown surface for filter options and
 * actions. Configure the popup contents by passing a `<FilterMenuBody>` as
 * children.
 *
 * ```tsx
 * <FilterMenu
 *   applied={!!selected}
 *   label="Size"
 *   onClear={() => setSelected(undefined)}
 * >
 *   <FilterMenuBody
 *     title="Size"
 *     clearActionLabel="Clear"
 *     footerActionProps={{ label: "Apply", onClick: handleApply }}
 *   >
 *     <Radio label="Small" name="size" />
 *     <Radio label="Large" name="size" />
 *   </FilterMenuBody>
 * </FilterMenu>
 * ```
 *
 * {@link https://www.notion.so/faire/Filter-Menu-3762efb5c25a80b69bc8c31c540bc726 | Slate/FilterMenu}
 */
export const FilterMenu: React.FC<IProps> = ({
  alignment = "start",
  applied = false,
  children,
  disabled = false,
  label,
  onClear,
  onClose,
  onClick,
  open,
  ref,
  ...props
}) => {
  const popupId = useId();
  const [{ body }, rest] = getSlots(children, { body: FilterMenuBody });

  if (rest.length > 0) {
    // oxlint-disable-next-line no-console
    console.error("FilterMenu only accepts FilterMenuBody as a child.");
  }

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        onClose?.();
      }
    },
    [onClose]
  );

  return (
    <FilterPopupRoot
      alignment={alignment}
      open={open}
      onOpenChange={handleOpenChange}
    >
      <FilterPopupChip
        applied={applied}
        aria-controls={popupId}
        disabled={disabled}
        label={label}
        onClear={onClear}
        onClick={onClick}
        ref={ref}
        {...props}
        data-component-name="Slate/FilterMenu"
      />
      <FilterMenuContext id={popupId} onClear={onClear}>
        <FilterPopupPortal>{body}</FilterPopupPortal>
      </FilterMenuContext>
    </FilterPopupRoot>
  );
};
